/**
 * CompetitionContent - Main Content Area Component
 *
 * Single responsibility: Rendering the main competition content.
 * Handles header, info, questions list, and footer display.
 */

import React from 'react';
import type { PickemDetailResponse } from '@bet99-pickem/api-types';
import type { SelectionState } from '@/hooks/usePickemSelections';
import { CompetitionHeader } from './CompetitionHeader';
import CompetitionInfo from './CompetitionInfo';
import StickyActionBar from './StickyActionBar';
import PickProgressBar from './PickProgressBar';
import { ResultsHeader } from './ResultsHeader';
import { QuestionRenderer } from './QuestionRenderer';
import { CompetitionFooter } from './CompetitionFooter';
import { QUESTION_TYPES } from '@/constants/question-types';

interface CompetitionContentProps {
  // Data
  pickemDetails: PickemDetailResponse | undefined;
  competitionData: any; // TODO: Proper typing from domain.ts
  timeDisplay: string;

  // Selections
  selections: SelectionState;
  selectionResults?: Record<string, boolean>;
  selectionPoints?: Record<string, number>;

  // Handlers
  onBack: () => void;
  onTeamSelect: (questionId: string, teamId: string | undefined) => void;
  onPropSelect: (questionId: string, answer: 'yes' | 'no') => void;
  onTieBreakerChange: (value: string) => void;
  onQuickPick: () => void;
  onClear: () => void;
  onSave: () => void;
  onLeaderboardClick: () => void;

  // State
  isViewOnly: boolean;
  isResultsMode: boolean;
  isSaving: boolean;
  canSubmit: boolean;
  submitButtonText: string;
}

/**
 * Component that renders the main competition content area
 */
export const CompetitionContent: React.FC<CompetitionContentProps> = ({
  pickemDetails,
  competitionData,
  timeDisplay,
  selections,
  selectionResults,
  selectionPoints,
  onBack,
  onTeamSelect,
  onPropSelect,
  onTieBreakerChange,
  onQuickPick,
  onClear,
  onSave,
  onLeaderboardClick,
  isViewOnly,
  isResultsMode,
  isSaving,
  canSubmit,
  submitButtonText,
}) => {
  // Sort questions: regular questions first, tiebreaker last
  const sortedQuestions =
    pickemDetails?.pickem.questions.slice().sort((a, b) => {
      const aIsTiebreaker = a.question_type?.toLowerCase() === 'tiebreaker';
      const bIsTiebreaker = b.question_type?.toLowerCase() === 'tiebreaker';

      if (aIsTiebreaker && !bIsTiebreaker) return 1;
      if (!aIsTiebreaker && bIsTiebreaker) return -1;

      return (a.order || 0) - (b.order || 0);
    }) || [];

  // Calculate answered questions for progress bar
  const answeredQuestions = sortedQuestions.map(question => {
    const questionType = question.question_type?.toLowerCase();

    if (questionType === QUESTION_TYPES.TIEBREAKER) {
      return selections.tieBreaker.trim() !== '';
    }

    const questionId = question.question_id;
    if (questionType === QUESTION_TYPES.PROP) {
      return !!selections.props[questionId];
    }

    return !!selections.teams[questionId];
  });

  // Get total number of questions (excluding tiebreaker) from transformed competitionData
  const totalQuestions = competitionData.questions.length;

  return (
    <div className="h-full bg-app-surface flex flex-col">
      <CompetitionHeader onBack={onBack} isComplete={competitionData.state === 'final'} />

      <div className="pt-16">
        {/* Competition Info - Scrolls away */}
        <CompetitionInfo
          title={competitionData.title}
          totalQuestions={totalQuestions}
          correctPicks={competitionData.correctPicks}
          gameDate={new Date(competitionData.gameDate)}
          state={competitionData.state}
        />

        {/* Sticky Action Bar - Only show when not in results/view mode */}
        {!isViewOnly && !isResultsMode && (
          <StickyActionBar onQuickPick={onQuickPick} onClear={onClear} />
        )}

        {/* Results Header (only show in results mode) */}
        {isResultsMode &&
          competitionData.finalScore &&
          typeof competitionData.finalScore.rank === 'number' &&
          typeof competitionData.finalScore.points === 'number' && (
            <ResultsHeader
              rank={competitionData.finalScore.rank}
              totalPoints={competitionData.finalScore.points}
              onLeaderboardClick={onLeaderboardClick}
            />
          )}

        {/* Questions List - Add bottom padding for fixed progress bar */}
        <div
          className={`flex-1 overflow-y-auto p-4 space-y-4 ${!isViewOnly && !isResultsMode ? 'pb-40' : ''}`}
        >
          {sortedQuestions.map((question, index) => {
            const isTiebreaker = question.question_type?.toLowerCase() === 'tiebreaker';
            const questionNumber = isTiebreaker ? undefined : index + 1;

            console.log(selections);

            return (
              <QuestionRenderer
                key={question.question_id}
                question={question}
                questionNumber={questionNumber}
                selectedTeams={selections.teams}
                selectedProps={selections.props}
                tieBreaker={selections.tieBreaker}
                onTeamSelect={onTeamSelect}
                onPropSelect={onPropSelect}
                onTieBreakerChange={onTieBreakerChange}
                isViewOnly={isViewOnly}
                selectionResults={isResultsMode ? selectionResults : undefined}
                selectionPoints={isResultsMode ? selectionPoints : undefined}
              />
            );
          })}
        </div>
      </div>

      {/* Pick Progress Bar - Fixed at bottom, only when not in results/view mode */}
      {!isViewOnly && !isResultsMode && (
        <PickProgressBar
          answeredQuestions={answeredQuestions}
          onSubmit={onSave}
          isSubmitting={isSaving}
        />
      )}

      {/* Footer - Only show in results mode for leaderboard button */}
      {isResultsMode && <CompetitionFooter onLeaderboardClick={onLeaderboardClick} />}
    </div>
  );
};
